import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FilmStrip, Envelope, LockKey, User, Briefcase, Heart } from '@phosphor-icons/react';
import { LEGAL_VERSION } from '../legal/content';
import { isNativeApp } from '../lib/native';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REF_LS_KEY = 'fz_ref_code';

const Register = ({ setUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'fan'
  });
  const [acks, setAcks] = useState({
    terms: false,
    privacy: false,
    risk: false,
    age: false,
  });
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fromUrl = searchParams.get('ref');
    const fromLs = localStorage.getItem(REF_LS_KEY);
    const code = (fromUrl || fromLs || '').trim().toUpperCase();
    if (code) {
      setReferralCode(code);
      localStorage.setItem(REF_LS_KEY, code);
    }
    // Pre-select role from ?role= URL param (lands user on the right CTA from the homepage)
    const roleParam = (searchParams.get('role') || '').trim().toLowerCase();
    if (['fan', 'investor', 'producer'].includes(roleParam)) {
      setFormData((f) => ({ ...f, role: roleParam }));
    }
  }, [searchParams]);

  const allAcked = acks.terms && acks.privacy && acks.risk && acks.age;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allAcked) {
      toast.error('Please tick every required acknowledgment to continue');
      return;
    }
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, {
        ...formData,
        legal_version: LEGAL_VERSION,
        accepted_terms: acks.terms,
        accepted_privacy: acks.privacy,
        accepted_risk: acks.risk,
        accepted_age: acks.age,
        referral_code: referralCode || undefined,
      });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.removeItem(REF_LS_KEY);
      setUser(response.data.user);
      toast.success(referralCode ? `Account created · referral applied (+1 Zinglet)` : 'Account created!');
      
      if (response.data.user.role === 'producer') {
        navigate('/producer-dashboard');
      } else if (response.data.user.role === 'fan') {
        navigate('/fan-dashboard');
      } else {
        navigate('/marketplace');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-background flex items-center justify-center px-4 py-12${isNativeApp() ? ' native-auth-screen' : ''}`}>
      <div className="noise-overlay" />
      
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-3 mb-6">
            <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10" data-testid="brand-logo" />
            <span className="font-heading text-2xl font-bold tracking-tight">Fan<span className="text-gold">Zingy</span></span>
          </div>
          <h1 className="font-heading text-4xl font-bold mb-2">JOIN FANZINGY</h1>
          <p className="font-body text-slate-400">Start investing in blockbuster movies</p>
        </div>

        <form onSubmit={handleSubmit} data-testid="register-form" className="bg-card border border-white/5 rounded-lg p-8">
          <div className="space-y-6">
            <div>
              <label className="font-body text-sm font-medium mb-2 block" htmlFor="name">
                Full Name
              </label>
              <div className="relative">
                <User size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="register-name-input"
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white placeholder:text-slate-400 pl-12 pr-4 font-body outline-none transition-colors"
                  placeholder="John Doe"
                />
              </div>
            </div>

            <div>
              <label className="font-body text-sm font-medium mb-2 block" htmlFor="email">
                Email
              </label>
              <div className="relative">
                <Envelope size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="register-email-input"
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white placeholder:text-slate-400 pl-12 pr-4 font-body outline-none transition-colors"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div>
              <label className="font-body text-sm font-medium mb-2 block" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <LockKey size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="register-password-input"
                  id="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white placeholder:text-slate-400 pl-12 pr-4 font-body outline-none transition-colors"
                  placeholder="Minimum 8 characters"
                />
              </div>
            </div>

            <div>
              <label className="font-body text-sm font-medium mb-2 block">
                I am a...
              </label>
              <div className="grid grid-cols-3 gap-3 native-role-grid">
                <button
                  data-testid="register-role-fan-btn"
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'fan' })}
                  className={`h-20 rounded-sm border transition-all ${
                    formData.role === 'fan'
                      ? 'border-gold bg-gold/10'
                      : 'border-white/10 bg-black/20 hover:bg-white/5'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <Heart size={24} className={formData.role === 'fan' ? 'text-gold' : 'text-slate-400'} weight={formData.role === 'fan' ? 'fill' : 'regular'} />
                    <span className="font-body text-sm mt-2">Fan</span>
                  </div>
                </button>
                <button
                  data-testid="register-role-investor-btn"
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'investor' })}
                  className={`h-20 rounded-sm border transition-all ${
                    formData.role === 'investor'
                      ? 'border-gold bg-gold/10'
                      : 'border-white/10 bg-black/20 hover:bg-white/5'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <Briefcase size={24} className={formData.role === 'investor' ? 'text-gold' : 'text-slate-400'} />
                    <span className="font-body text-sm mt-2">Investor</span>
                  </div>
                </button>
                <button
                  data-testid="register-role-producer-btn"
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'producer' })}
                  className={`h-20 rounded-sm border transition-all ${
                    formData.role === 'producer'
                      ? 'border-gold bg-gold/10'
                      : 'border-white/10 bg-black/20 hover:bg-white/5'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <FilmStrip size={24} className={formData.role === 'producer' ? 'text-gold' : 'text-slate-400'} />
                    <span className="font-body text-sm mt-2">Producer</span>
                  </div>
                </button>
              </div>
            </div>

            <div className="space-y-2 pt-2 border-t border-white/8" data-testid="register-legal-acks">
              {referralCode && (
                <div className="bg-[#facc15]/12 border border-[#facc15]/40 rounded-lg px-3 py-2 mb-2 flex items-center gap-2" data-testid="referral-applied-banner">
                  <span className="text-[10px] uppercase tracking-wider text-[#facc15] font-bold">Referral code</span>
                  <code className="text-sm font-mono font-extrabold text-[#facc15]">{referralCode}</code>
                  <span className="text-[10px] text-white/60 font-light ml-auto">+1 Zinglet for you on signup</span>
                </div>
              )}
              <p className="text-[10px] uppercase tracking-wider text-white/70 font-bold mb-1">Required acknowledgments</p>
              <AckRow id="ack-age" testid="ack-age" checked={acks.age} onChange={(v) => setAcks({ ...acks, age: v })}>
                I am at least <strong>18 years</strong> old and have full legal capacity to enter this agreement in my country of residence.
              </AckRow>
              <AckRow id="ack-terms" testid="ack-terms" checked={acks.terms} onChange={(v) => setAcks({ ...acks, terms: v })}>
                I have read and accept the <Link to="/legal/terms" target="_blank" className="text-gold underline">Terms of Service</Link> and <Link to="/legal/content" target="_blank" className="text-gold underline">Content Policy</Link>.
              </AckRow>
              <AckRow id="ack-privacy" testid="ack-privacy" checked={acks.privacy} onChange={(v) => setAcks({ ...acks, privacy: v })}>
                I agree to the <Link to="/legal/privacy" target="_blank" className="text-gold underline">Privacy &amp; Data-Usage Policy</Link> and to identity verification (KYC) under <Link to="/legal/aml" target="_blank" className="text-gold underline">PMLA / FinCEN / OFAC</Link>.
              </AckRow>
              <AckRow id="ack-risk" testid="ack-risk" checked={acks.risk} onChange={(v) => setAcks({ ...acks, risk: v })}>
                I understand that <strong>Fanzingy is only a marketplace</strong>; capital flows through a Partner NBFC and not Fanzingy. I have read the <Link to="/legal/risk" target="_blank" className="text-gold underline">Risk Disclosure</Link> and accept that <strong>film investments are subject to risk</strong>.
              </AckRow>
            </div>

            <button
              data-testid="register-submit-btn"
              type="submit"
              disabled={loading || !allAcked}
              className="w-full btn-primary py-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating account...' : (allAcked ? 'Create Account' : 'Tick every acknowledgment above')}
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="font-body text-slate-400">
              Already have an account?{' '}
              <Link to="/login" data-testid="register-login-link" className="text-gold hover:text-gold-hover font-medium transition-colors">
                Login
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;

const AckRow = ({ id, testid, checked, onChange, children }) => (
  <label htmlFor={id} className="flex items-start gap-3 cursor-pointer text-[13px] text-slate-300 leading-relaxed py-2">
    <input
      id={id}
      data-testid={testid}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 w-5 h-5 accent-gold flex-shrink-0"
    />
    <span>{children}</span>
  </label>
);